import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MainLayout } from "@/layouts/MainLayout";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { SearchBar } from "@/components/SearchBar";
import { EventCard } from "@/components/cards/EventCard";
import { LiveCard } from "@/components/cards/LiveCard";
import { LoadingState } from "@/components/states/LoadingState";
import { EmptyState } from "@/components/states/EmptyState";
import { ErrorState } from "@/components/states/ErrorState";
import { CATEGORIES } from "@/lib/mockData";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export default function Explore() {
  const [searchParams] = useSearchParams();
  const initialCategory = CATEGORIES.includes(searchParams.get("category"))
    ? searchParams.get("category")
    : "All";
  const [query, setQuery] = useState(searchParams.get("query") || "");
  const [category, setCategory] = useState(initialCategory);
  const [tab, setTab] = useState("events");

  const events = useMockQuery(
    () => apiClient.getEvents({ category, query }),
    [category, query]
  );
  const lives = useMockQuery(() => apiClient.getLiveStreams(), []);

  return (
    <MainLayout>
      <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
        <div className="mb-8 max-w-2xl">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
            Explore
          </h1>
          <p className="mt-3 text-lg text-muted-foreground">
            Search live streams and events by city, category, or vibe.
          </p>
        </div>

        <div className="mb-8 space-y-5">
          <SearchBar value={query} onChange={setQuery} className="max-w-xl" />
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200",
                  category === c
                    ? "border-brand bg-brand text-white"
                    : "border-border bg-card text-muted-foreground hover:border-brand/40 hover:text-foreground"
                )}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-8 rounded-full bg-secondary p-1">
            <TabsTrigger
              value="events"
              data-testid="tab-events"
              className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm"
            >
              Events
            </TabsTrigger>
            <TabsTrigger
              value="live"
              data-testid="tab-live"
              className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm"
            >
              Live now
            </TabsTrigger>
          </TabsList>

          {tab === "events" && (
            <>
              {events.loading ? (
                <LoadingState count={6} />
              ) : events.error ? (
                <ErrorState error={events.error} onRetry={events.refetch} />
              ) : events.isEmpty ? (
                <EmptyState
                  title="No events match your search"
                  description="Try a different keyword or category to find events near you."
                  actionLabel="Clear filters"
                  onAction={() => {
                    setQuery("");
                    setCategory("All");
                  }}
                />
              ) : (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {events.data.map((e) => (
                    <EventCard key={e.id} event={e} />
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "live" && (
            <>
              {lives.loading ? (
                <LoadingState count={3} />
              ) : lives.error ? (
                <ErrorState error={lives.error} onRetry={lives.refetch} />
              ) : lives.isEmpty ? (
                <EmptyState title="No one is live right now" description="Check back soon — creators go live throughout the day." />
              ) : (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {lives.data.map((s) => (
                    <LiveCard key={s.id} stream={s} />
                  ))}
                </div>
              )}
            </>
          )}
        </Tabs>
      </div>
    </MainLayout>
  );
}
